import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FaArrowLeft, FaCalendarAlt, FaEye, FaStar, FaUser, FaClock,
  FaBullhorn, FaGraduationCap, FaLaptop, FaBible, FaNewspaper
} from 'react-icons/fa';
import noticiasMuralService from '../../services/noticiasMuralService';

// Mapeamento de ícones
const iconMap = {
  FaBullhorn: FaBullhorn,
  FaCalendarAlt: FaCalendarAlt,
  FaGraduationCap: FaGraduationCap,
  FaLaptop: FaLaptop,
  FaBible: FaBible,
  FaStar: FaStar,
  FaNewspaper: FaNewspaper,
};

/**
 * Página de visualização de uma notícia
 */
const NoticiaView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [noticia, setNoticia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar notícia
  useEffect(() => {
    const carregarNoticia = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await noticiasMuralService.buscarPorId(id);
        
        if (response.success) {
          setNoticia(response.data);
          
          // Registrar visualização (silenciosamente)
          noticiasMuralService.registrarVisualizacao(id);
        } else {
          setError('Notícia não encontrada');
        }
      } catch (error) {
        console.error('Erro ao carregar notícia:', error);
        setError('Erro ao carregar notícia');
      } finally {
        setLoading(false);
      }
    };

    carregarNoticia();
  }, [id]);

  // Formatar data completa
  const formatarDataCompleta = (dataString) => {
    if (!dataString) return '';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Renderizar ícone da categoria
  const renderCategIcon = (icone, cor) => {
    const IconComponent = iconMap[icone] || FaNewspaper;
    return <IconComponent className="w-5 h-5" style={{ color: cor }} />;
  };

  // Loading
  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-gray-200 rounded mb-6"></div>
            <div className="h-64 bg-gray-200 rounded-lg mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Erro
  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/mural')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <FaArrowLeft className="w-4 h-4" />
            Voltar ao mural
          </button>
          
          <div className="bg-red-50 text-red-700 p-6 rounded-lg text-center">
            <h2 className="text-lg font-semibold mb-2">Erro</h2>
            <p>{error}</p>
            <button
              onClick={() => navigate('/mural')}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Voltar ao mural
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Voltar */}
        <button
          onClick={() => navigate('/mural')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <FaArrowLeft className="w-4 h-4" />
          Voltar ao mural
        </button>

        {/* Artigo */}
        <article className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Imagem de capa */}
          {noticia.imagem_capa && (
            <div className="h-64 md:h-80 overflow-hidden">
              <img 
                src={noticia.imagem_capa} 
                alt={noticia.titulo}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 md:p-8">
            {/* Categoria e destaque */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {renderCategIcon(noticia.categoria_icone, noticia.categoria_cor)}
                <span 
                  className="text-sm font-medium"
                  style={{ color: noticia.categoria_cor || '#6b7280' }}
                >
                  {noticia.categoria_nome || 'Geral'}
                </span>
              </div>
              {noticia.destaque === 1 && (
                <span className="flex items-center gap-1 text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                  <FaStar className="w-4 h-4" />
                  Destaque
                </span>
              )}
            </div>

            {/* Título */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {noticia.titulo}
            </h1>

            {/* Subtítulo */}
            {noticia.subtitulo && (
              <p className="text-lg text-gray-600 mb-6">
                {noticia.subtitulo}
              </p>
            )}

            {/* Meta informações */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100">
              <span className="flex items-center gap-1.5">
                <FaCalendarAlt className="w-4 h-4" />
                {formatarDataCompleta(noticia.data_publicacao || noticia.created_at)}
              </span>
              {noticia.autor_nome && (
                <span className="flex items-center gap-1.5">
                  <FaUser className="w-4 h-4" />
                  {noticia.autor_nome}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <FaEye className="w-4 h-4" />
                {noticia.visualizacoes || 0} visualizações
              </span>
            </div>

            {/* Conteúdo */}
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: noticia.conteudo }}
            />
          </div>
        </article>

        {/* Navegação inferior */}
        <div className="mt-6 flex justify-between">
          <Link
            to="/mural"
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
          >
            <FaArrowLeft className="w-4 h-4" />
            Ver todas as notícias
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NoticiaView;
